import { findStepType } from '@vigeo/shared';
import type { PacketStep } from './api.js';

/**
 * Is this step the candidate's to do?
 *
 * Two reasons it might not be. It may be finished — submitted or approved — in which case
 * showing it as a task is wrong and showing its editor is worse: a `disabled` file chooser
 * on an approved document still reads as something to act on. Or it may not be theirs at all.
 *
 * THE SECOND TEST MOVED. It used to ask the WEB registry whether a type had a person-facing
 * half (`portal: null`), which conflated two different questions: "is there anything to
 * render" and "whose work is this". The external compliance checkpoint separated them — it
 * has a portal half, because a candidate who cannot see it is left wondering why their file
 * is incomplete, but it is the office's to move and the server refuses a person-submitted
 * office step.
 *
 * Asking the KERNEL's `submittedBy` is both more accurate and no longer a client-only rule:
 * the same fact now drives the server's refusal, so the portal and the API cannot disagree
 * about whose work a step is. `check-api-purity` forbids apps/api importing the web registry,
 * which is why the old version had to live here; the kernel has no such restriction.
 */
export function isOutstanding(step: PacketStep): boolean {
  if (findStepType(step.type)?.submittedBy !== 'person') return false;
  return step.state === 'not_started' || step.state === 'sent' || step.state === 'in_progress' || step.state === 'rejected';
}

/**
 * Did the CANDIDATE send this?
 *
 * The completion screen lists these under "What we have from you", and that heading is a
 * claim. An office-recorded item — orientation HR ran in a room, a background check result
 * that arrived by email — was never sent by this person, so listing it there tells them
 * something untrue about their own file and invites a question the portal cannot answer.
 *
 * Same person-facing test as `isOutstanding`, so the two halves of the packet are split on
 * one rule rather than two that can drift apart: anything with no portal half is the
 * office's work at both ends of the process.
 */
export function wasSentByCandidate(step: PacketStep): boolean {
  if (findStepType(step.type)?.submittedBy !== 'person') return false;
  return step.state === 'submitted' || step.state === 'approved';
}

/**
 * Should the candidate SEE this step, even though it is not theirs to do?
 *
 * The third category, and it exists because the first two do not cover an obligation that is
 * genuinely the person's responsibility but happens in another system. `isOutstanding` says
 * no — the office moves it, and the portal must never offer a control for it. `wasSentByCandidate`
 * says no — they sent nothing. So the external I-9 checkpoint was in the packet, rendered
 * nowhere, and a candidate who had done everything the portal asked was left to wonder why
 * their file was still incomplete. That is exactly the gap the step type was written to close.
 *
 * Two conditions, both required. The office owns it (`submittedBy`, the kernel's fact, the
 * same one the server enforces), and there is something to render: a type with no portal half
 * is HR's business at both ends and telling a candidate about it would only raise a question
 * the portal cannot answer.
 */
export function isElsewhere(step: PacketStep, hasPortalHalf: (type: string) => boolean): boolean {
  /**
   * Written as "not the person's" rather than "the office's" for two reasons. The seeded
   * catalog has a job profile keyed `office`, so a bare `'office'` literal here is
   * indistinguishable from naming that row and `check:requirement-keys` rightly refuses it —
   * and this way an unknown step type falls out as NOT the candidate's business, which is the
   * safer of the two readings.
   */
  const kernel = findStepType(step.type);
  if (kernel === undefined || kernel.submittedBy === 'person') return false;
  if (step.state === 'cancelled') return false;
  return hasPortalHalf(step.type);
}
